import React, { useEffect, useState } from 'react';
import { Edit } from '@mui/icons-material';
import {
  useTheme,
  Grid,
  Paper,
  Typography,
  Box,
  List,
  ListItemText,
  ListItem,
  Chip,
  Button,
  Badge,
  styled,
} from '@mui/material';
import { getCompetitionById } from '../../services/Competition';
import { formatDate } from '../../../utils/TimeFormat';
import { getImageUrlAdminToken } from '../../utills/ImagesUtilsAdmin';
import { useNavigate } from 'react-router-dom';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const IndividualCompDetail = ({ competitionId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [competition, setCompetition] = useState(null);

  useEffect(() => {
    if (competitionId) {
      const fetchCompetition = async () => {
        try {
          const data = await getCompetitionById(competitionId);
          console.log('Fetched competition data:', data);
          setCompetition(data.competition);
        } catch (error) {
          console.error('Error fetching competition details:', error);
        }
      };
      fetchCompetition();
    }
  }, [competitionId]);

  if (!competition) return null;

  // Parse rules array safely (since it's a stringified array in the API)
  const rules = (() => {
    try {
      return JSON.parse(competition.competition_rules || '[]');
    } catch {
      return [];
    }
  })();

  // Parse required_attributes if needed
  const requiredAttributes = (() => {
    try {
      return JSON.parse(competition.required_attributes || '{}');
    } catch {
      return {};
    }
  })();

  // Parse participant_attributes safely
  const participantAttributes = (() => {
    try {
      return JSON.parse(competition.participant_attributes || '{}');
    } catch {
      return {};
    }
  })();

  const hasParticipantAttributes = Object.keys(participantAttributes).length > 0;

  const statusConfig = {
    ongoing: { color: 'success', label: 'Ongoing' },
    upcoming: { color: 'warning', label: 'Upcoming' },
    ended: { color: 'info', label: 'Ended' }
  };

  const { color: statusColor, label: statusLabel } = statusConfig[competition?.status.toLowerCase()] || { color: 'primary', label: competition?.status };

  return (
    <Grid container spacing={2} mt={2}>
      {/* LEFT PANEL */}
      <Grid size={{ xs: 12, md: 8 }}>
        <Paper
          elevation={0}
          sx={{ p: 2, gap: 2, display: 'flex', flexDirection: 'column' }}
        >
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" gutterBottom>
              {competition.competition_name}
            </Typography>
            <Button
              onClick={() => navigate(`/competition/${competitionId}/edit`)}
              disabled={competition.status === 'inactive'}
              variant="contained"
              disableElevation
              color="primary"
              size='small'
              startIcon={<Edit />}
              sx={{ textTransform: 'none' }}
            >
              Edit
            </Button>
          </Box>

          <Box sx={{ position: 'relative' }}>
            <Box
              component="img"
              src={getImageUrlAdminToken(competition.banner)}
              alt="Competition Banner"
              sx={{ width: '100%', height: 'auto', objectFit: 'cover', borderRadius: 2 }}
            />
            <Chip
              label={statusLabel}
              color={statusColor}
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                fontWeight: 600,
                backgroundColor: theme.palette[statusColor]?.main,
              }}
              clickable={false}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>

          <Typography variant="body1">
            <strong>Description:</strong> {competition.competition_description}
          </Typography>
          <Typography variant="body1">
            <strong>Prize:</strong> {competition.competition_prize_details}
          </Typography>

          {/* Rules */}
          {rules.length > 0 && (
            <Box
              sx={{
                bgcolor: theme.palette.background.default,
                p: 2,
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" color="primary" gutterBottom fontWeight="bold">
                Rules and Guidelines
              </Typography>
              <List dense>
                {rules.map((rule, idx) => (
                  <ListItem key={idx} disablePadding>
                    <ListItemText primary={`${idx + 1}. ${rule}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* RIGHT PANEL */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Grid container spacing={2}>
          {/*participant_attributes*/}
          {hasParticipantAttributes && (
            <Grid size={12}>
              <Paper elevation={0} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <StyledBadge
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  variant="dot"
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Evaluator Rewards Activated
                  </Typography>
                </StyledBadge>
                <Typography variant="body2">
                  <strong>Criteria Function:</strong> {participantAttributes.criteria_function || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>Tracking Window:</strong> {participantAttributes.tracking_window.charAt(0).toUpperCase() + participantAttributes.tracking_window.slice(1) || '-'}
                </Typography>
              </Paper>
            </Grid>
          )}


          {/* Module */}
          <Grid size={12}>
            <Paper elevation={0}>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Module"
                    secondary={competition.module || '-'}
                    slotProps={{
                      primary: { sx: { fontWeight: 'bold', fontSize: '0.9rem' } },
                    }}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          {/* Tracking Info */}
          <Grid size={12}>
            <Paper elevation={0} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>Tracking Attribute:</strong> {competition.tracking_attribute || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Tracking Window:</strong> {competition.tracking_window || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Criteria Function:</strong>
                <Typography component={"span"} variant="body2" sx={{ textTransform: 'uppercase' }}> {competition.criteria_function.slice(0).replace('_', ' ') || '-'}</Typography>
              </Typography>
            </Paper>
          </Grid>

          {/* Competition Dates */}
          <Grid size={6}>
            <Paper elevation={0}>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Competition Date"
                    secondary={`${formatDate(competition.competition_start_date)} - ${formatDate(
                      competition.competition_end_date
                    )}`}
                    slotProps={{
                      primary: { sx: { fontWeight: 'bold', fontSize: '0.9rem' } },
                    }}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          {/* Submission Dates */}
          <Grid size={6}>
            <Paper elevation={0}>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Submission Date"
                    secondary={`${formatDate(competition.enrollment_start_date)} - ${formatDate(
                      competition.enrollment_end_date
                    )}`}
                    slotProps={{
                      primary: { sx: { fontWeight: 'bold', fontSize: '0.9rem' } },
                    }}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>

        </Grid>
      </Grid>
    </Grid>
  );
};

export default IndividualCompDetail;
